/**
 * GuildDetailModal.jsx — quick stats pop-up for a single guild.
 *
 * Issue #92 — companion to ItemDetailModal / UserDetailModal so guild
 * picks (`v2-guild-pick`) surface a popup like the other detail kinds,
 * with a corner ⤢ affordance to promote into a transient window.
 *
 * Pulls the same `guild-detail` widget endpoint used by the GuildDetail
 * widget renderer; rendering is delegated to GuildDetailBody so the popup
 * and the "open in window" promotion share the same visual layer.
 */

import { createSignal, createEffect, Show, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import api from '../lib/apiClient.js';
import wm from '../lib/wmStore.js';
import { toast } from '../lib/toastStore.js';
import GuildDetailBody from './GuildDetailBody.jsx';
import { EMOJI } from '../lib/emoji.js';
import './v2-modal.css';

export default function GuildDetailModal() {
  const [name,    setName]    = createSignal(null);
  const [guildId, setGuildId] = createSignal(null);
  const [weekOffset, setWeekOffset] = createSignal(0);
  const [data,    setData]    = createSignal(null);
  const [loading, setLoading] = createSignal(false);
  const [error,   setError]   = createSignal(null);

  function open(detail) {
    if (!detail) return;
    if (!detail.name && !detail.guildId) return;
    setName(detail.name || null);
    setGuildId(detail.guildId || null);
    setWeekOffset(Number.isFinite(Number(detail.weekOffset)) ? Math.max(0, Math.min(59, Number(detail.weekOffset))) : 0);
    setData(null);
    setError(null);
  }
  function close() {
    setName(null); setGuildId(null); setWeekOffset(0); setData(null); setError(null);
  }

  // Issue #92 — promote the popup to a transient window (open in window).
  function promoteToWindow() {
    const id = wm.addTransientWidget('guild-detail', {
      name: name() || '',
      guildId: guildId() || null,
      weekOffset: weekOffset(),
    });
    if (id) {
      toast.info('Opened as window', { ttl: 1400 });
      close();
    }
  }

  createEffect(() => {
    const n = name();
    const g = guildId();
    const w = weekOffset();
    if (!n && !g) return;
    setLoading(true);
    api.fetchWidget('guild-detail', { name: n, guildId: g, weekOffset: w })
      .then((r) => { setData(r?.payload || null); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  });

  function onPick(ev) { open(ev?.detail); }
  function onKey(ev) { if (ev.key === 'Escape') close(); }
  onMount(() => {
    window.addEventListener('v2-guild-pick', onPick);
    window.addEventListener('keydown', onKey);
  });
  onCleanup(() => {
    window.removeEventListener('v2-guild-pick', onPick);
    window.removeEventListener('keydown', onKey);
  });

  return (
    <Show when={name() || guildId()}>
      <Portal>
        <div class="v2-modal__backdrop" onClick={close}>
          <div class="v2-modal" onClick={(e) => e.stopPropagation()}>
            <header class="v2-modal__hdr">
              <span class="v2-modal__emoji">{EMOJI.guild}</span>
              <span class="v2-modal__title">{name() || `Guild #${guildId()}`}</span>
              <button
                class="v2-btn-sm v2-modal__expand"
                onClick={promoteToWindow}
                title="Open as window"
                aria-label="Open as window"
              >⤢</button>
              <button class="v2-btn-sm" onClick={close} title="Close (Esc)">✖</button>
            </header>
            <GuildDetailBody
              name={name()}
              data={data()}
              loading={loading()}
              error={error()}
              weekOffset={weekOffset()}
            />
          </div>
        </div>
      </Portal>
    </Show>
  );
}
